(() => {
  const root = document.documentElement;
  let pendingTimer;
  let navigationAttempt = 0;

  function resetNavigation() {
    navigationAttempt += 1;
    clearTimeout(pendingTimer);
    root.removeAttribute("data-navigation-pending");
  }

  function beginNavigation(event) {
    const attempt = ++navigationAttempt;
    setTimeout(() => {
      if (attempt !== navigationAttempt || event.defaultPrevented) return;
      clearTimeout(pendingTimer);
      root.setAttribute("data-navigation-pending", "");
      pendingTimer = setTimeout(resetNavigation, 15000);
    }, 0);
  }

  // Menu summaries navigate through JavaScript rather than an anchor click.
  // Their component announces only when it actually follows the destination.
  document.addEventListener("theme:navigation:start", event => {
    if (event.target.closest?.("[data-no-navigation-feedback]")) return;
    const url = internalURL(event.detail?.url);
    if (!url || (url.pathname === location.pathname && url.search === location.search)) return;
    navigationAttempt += 1;
    clearTimeout(pendingTimer);
    root.setAttribute("data-navigation-pending", "");
    pendingTimer = setTimeout(resetNavigation, 15000);
  });

  function navigatesHere(target) {
    const effectiveTarget = target ?? document.querySelector("base[target]")?.getAttribute("target");
    return !effectiveTarget || effectiveTarget.toLowerCase() === "_self";
  }

  function internalURL(href) {
    if (typeof href !== "string" || !href) return null;
    try {
      const url = new URL(href, document.baseURI);
      return url.origin === location.origin && /^https?:$/.test(url.protocol) ? url : null;
    } catch {
      return null;
    }
  }

  document.addEventListener("click", (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.composedPath().find((node) => node.matches?.("a[href]"));
    if (!link || link.hasAttribute("download") || !navigatesHere(link.getAttribute("target"))) return;
    if (link.closest("[data-no-navigation-feedback]")) return;
    const url = internalURL(link.href);
    if (!url || (url.pathname === location.pathname && url.search === location.search)) return;
    beginNavigation(event);
  }, true);

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.closest("[data-no-navigation-feedback]")) return;
    const submitter = event.submitter;
    const method = submitter?.getAttribute("formmethod") ?? form.getAttribute("method") ?? "get";
    const target = submitter?.getAttribute("formtarget") ?? form.getAttribute("target");
    const action = submitter?.getAttribute("formaction") ?? form.getAttribute("action") ?? location.href;
    if (method.toLowerCase() === "get" && navigatesHere(target) && internalURL(action)) beginNavigation(event);
  }, true);

  window.addEventListener("pageshow", resetNavigation);
  window.addEventListener("pagehide", resetNavigation);
  window.navigation?.addEventListener("navigateerror", resetNavigation);

  const connection = navigator.connection;
  if (window.Shopify?.designMode ||
      connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "")) return;

  const storefrontRoot = new URL(window.Shopify?.routes?.root || "/", location.origin);
  const supportsRules = HTMLScriptElement.supports?.("speculationrules");
  // Menu headings navigate through data-follow-link, so anchor-based browser
  // rules (and the vendor's anchor prefetcher) cannot discover their targets.
  const summarySelector = ".header summary[data-follow-link]";
  const warmedDestinations = new Map();
  let hoverTimer;
  function prefetchDestination(element) {
    const href = element.getAttribute("data-follow-link") || element.getAttribute("href");
    if (!href || element.closest("[data-no-prefetch], [data-no-instant]") ||
        element.matches('[download], [aria-controls], [role="button"]') ||
        !navigatesHere(element.getAttribute("target"))) return false;
    const url = internalURL(href);
    if (!url || !url.pathname.startsWith(storefrontRoot.pathname) ||
        !/^(products|collections|pages|blogs)\//.test(url.pathname.slice(storefrontRoot.pathname.length)) ||
        url.searchParams.has("section_id") || url.searchParams.has("sections") ||
        (url.pathname === location.pathname && url.search === location.search)) return false;
    url.hash = "";
    if (warmedDestinations.has(url.href)) return false;
    const hint = document.createElement(supportsRules ? "script" : "link");
    if (supportsRules) {
      hint.type = "speculationrules";
      hint.textContent = JSON.stringify({ prefetch: [{ urls: [url.href], eagerness: "immediate" }] });
    } else {
      if (!hint.relList.supports("prefetch")) return false;
      hint.rel = "prefetch";
      hint.as = "document";
      hint.href = url.href;
    }
    // Keep only the two most recently intended menu destinations warm.
    if (warmedDestinations.size === 2) {
      const oldest = warmedDestinations.keys().next().value;
      warmedDestinations.get(oldest).remove();
      warmedDestinations.delete(oldest);
    }
    hint.dataset.menuPrefetch = "";
    document.head.append(hint);
    warmedDestinations.set(url.href, hint);
    return true;
  }
  document.addEventListener("pointerover", event => {
    const summary = event.target.closest?.(summarySelector);
    if (!summary || event.pointerType === "touch" || summary.contains(event.relatedTarget)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => {
      if (summary.isConnected) prefetchDestination(summary);
    }, 100);
  }, { passive: true });
  document.addEventListener("pointerout", event => {
    const summary = event.target.closest?.(summarySelector);
    if (summary && !summary.contains(event.relatedTarget)) clearTimeout(hoverTimer);
  }, { passive: true });
  for (const type of ["focusin", "pointerdown"]) {
    document.addEventListener(type, event => {
      const summary = event.target.closest?.(summarySelector);
      if (!summary || (type === "pointerdown" && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey))) return;
      clearTimeout(hoverTimer);
      // A mouse click is already navigating; a new prefetch here can race the
      // navigation and download the same HTML twice. Keep keyboard/touch intent.
      if (type === "pointerdown" && event.pointerType !== "touch") return;
      if (type === "focusin" && !summary.matches(":focus-visible")) return;
      prefetchDestination(summary);
    }, { passive: true });
  }
  window.addEventListener("pagehide", () => clearTimeout(hoverTimer));
  document.addEventListener("theme:navigation:start", () => clearTimeout(hoverTimer));

  // Give quick clicks a head start too. Once this page has loaded, use idle
  // time to fetch only the first two visible primary-menu destinations.
  // Keep slower connections on intent-only warming.
  function warmPrimaryNavigation() {
    if (document.visibilityState !== "visible" || root.hasAttribute("data-navigation-pending") || warmedDestinations.size) return;
    let count = 0;
    for (const item of document.querySelectorAll(".header__primary-nav-item")) {
      const element = item.querySelector("summary[data-follow-link], a[href]");
      if (!element) continue;
      const bounds = element.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0 || bounds.bottom <= 0 || bounds.top >= innerHeight || bounds.right <= 0 || bounds.left >= innerWidth) continue;
      if (prefetchDestination(element) && ++count === 2) break;
    }
  }
  if ((!connection?.effectiveType || connection.effectiveType === "4g") &&
      window.matchMedia?.("(hover: hover) and (pointer: fine)").matches) {
    const scheduleWarmup = () => {
      if (!document.querySelector(".header__primary-nav-item")) return;
      if (window.requestIdleCallback) requestIdleCallback(warmPrimaryNavigation, { timeout: 1000 });
      else setTimeout(warmPrimaryNavigation, 250);
    };
    if (document.readyState === "complete") scheduleWarmup();
    else window.addEventListener("load", scheduleWarmup, { once: true });
  }
  // A cached destination contains the cart header/drawer from its fetch time.
  document.addEventListener("cart:change", () => {
    clearTimeout(hoverTimer);
    for (const hint of warmedDestinations.values()) hint.remove();
    warmedDestinations.clear();
  });

  if (!supportsRules) return;
  const rules = document.createElement("script");
  rules.type = "speculationrules";
  rules.id = "glowid-navigation-prefetch";
  const intentRule = {
    eagerness: "moderate",
    where: {
      and: [
        { selector_matches: "a[data-instant], .header a[href], header-sidebar a[href]" },
        { href_matches: ["products/*", "collections/*", "pages/*", "blogs/*"].map((path) => new URL(path, storefrontRoot).href) },
        { not: { selector_matches: '[download], [target]:not([target=""]):not([target="_self"]), [aria-controls], [role="button"], [data-no-prefetch], [data-no-prefetch] a, [href*="section_id="], [href*="sections="]' } },
      ],
    },
  };
  const prefetch = [intentRule];
  // Touch navigation has little hover lead time. Warm only the first two
  // collection products while the shopper reads, using the browser's cache
  // and native concurrency limits. Avoid extra work on slower connections.
  if (!connection?.effectiveType || connection.effectiveType === "4g") {
    prefetch.push({
      eagerness: "eager",
      where: { and: [intentRule.where, {
        selector_matches: '.shopify-section--main-collection product-list > product-card:nth-child(-n+2) a[data-instant]',
      }] },
    });
  }
  rules.textContent = JSON.stringify({ prefetch });
  document.head.append(rules);
})();
